import React from "react";
import clsx from "clsx";

import type {
    AgGridReact,
    Column,
    FocusGridInnerElementParams,
    GetRowIdFunc,
    GridProps,
} from "@/ui/molecules/Grid";
import Grid from "@/ui/molecules/Grid";

import { useExpandableSubcategories } from "../useExpandableSubcategories";

import { makeGridReviewColumnDefs } from "./makeGridReviewColumnDefs";

import type {
    TActiveColumnChange,
    TColumnDefsOperatingStatements,
    TRowReviewWithOperatingStatements,
    TRowTotalPinned,
} from "../typesGrids";
import type { ExtractionsErrorListProps } from "../ExtractionsSidebar/ExtractionsErrorList";

export type GridReviewProps = Omit<
    GridProps<TRowReviewWithOperatingStatements>,
    "columnDefs" | "rowData" | "readOnlyEdit" | "onCellEditRequest"
> & {
    isExtractionCompleted: boolean;
    columnDefsOperatingStatements: TColumnDefsOperatingStatements;
    reviewData: TRowReviewWithOperatingStatements[];
    standardizedTotalNetIncomeOriginal: TRowTotalPinned | null;
    standardizedOriginalNetIncomeTotalName: string | null;
    standardizedTotalNetIncomeAltered: TRowTotalPinned | null;
    allowEditing: boolean;
    errorGroups: ExtractionsErrorListProps["errors"];
    activeColumns: Record<string, TActiveColumnChange> | null;
    extractionsInfo:
        | {
              fileName: string;
              extractionId: number;
              sheetName: string;
          }[]
        | null;
    longestOperatingStatementAndTotalsNumericValue: number;
    extractionId: number;
    setActiveColumn: (label: string, extractionId: number) => void;
};

const disableRowAnimationOnZooming = {
    "& .ag-row-animation .ag-row": {
        transition: "none",
    },
};

const makeOverlappingColumnHeadersWhite = {
    ".ag-header-row-column-group + .ag-header-row > div[class='ag-header-cell ag-focus-managed']:not(:hover), .ag-header-row-column-group + .ag-header-row > div[class='ag-header-cell ag-focus-managed ag-header-active']:not(:hover), .ag-header-row-column-group + .ag-header-row > div[class='ag-header-cell ag-focus-managed ag-header-active ag-column-resizing']:not(:hover)":
        {
            backgroundColor: "var(--proda-white) !important",
        },
    ".ag-header-row-column-group + .ag-header-row > div[class='ag-header-cell ag-focus-managed']:hover, .ag-header-row-column-group + .ag-header-row > div[class='ag-header-cell ag-focus-managed ag-header-active']:hover, .ag-header-row-column-group + .ag-header-row > div.ag-header-cell.ag-focus-managed.ag-column-resizing.ag-header-active:hover":
        {
            backgroundColor: "var(--proda-white) !important",
        },
};

const extraStyles = {
    "& .ag-row-level-0": {
        backgroundColor: "var(--proda-white) !important",
    },
};

const testId = "GridReview";
const GridReview = React.memo(
    React.forwardRef(
        (props: GridReviewProps, ref: React.ForwardedRef<AgGridReact>) => {
            const {
                isExtractionCompleted,
                allowEditing,
                reviewData,
                columnDefsOperatingStatements:
                    columnDefsOperatingStatementsFromProps,
                standardizedTotalNetIncomeOriginal,
                standardizedOriginalNetIncomeTotalName,
                standardizedTotalNetIncomeAltered,
                errorGroups,
                activeColumns,
                extractionsInfo,
                longestOperatingStatementAndTotalsNumericValue,
                sx,
                setActiveColumn,
                extractionId,
                ...otherProps
            } = props;

            const columnDefs = React.useMemo(
                () =>
                    makeGridReviewColumnDefs({
                        isExtractionCompleted,
                        allowEditing,
                        columnDefsOperatingStatementsFromProps,
                        longestOperatingStatementAndTotalsNumericValue,
                        errorGroups,
                        activeColumns,
                        extractionsInfo,
                        extractionId,
                        setActiveColumn,
                    }),
                [
                    isExtractionCompleted,
                    allowEditing,
                    columnDefsOperatingStatementsFromProps,
                    longestOperatingStatementAndTotalsNumericValue,
                    errorGroups,
                    activeColumns,
                    extractionsInfo,
                    extractionId,
                    setActiveColumn,
                ],
            );
            const focusFirstCellInAccountColumn = React.useCallback(
                (
                    params: FocusGridInnerElementParams<TRowReviewWithOperatingStatements>,
                ) => {
                    const column = params.api.getColumn("account") as Column;
                    params.api.setFocusedCell(0, column, null);
                    return true;
                },
                [],
            );

            const getRowId: GetRowIdFunc<TRowReviewWithOperatingStatements> =
                React.useCallback(params => String(params.data.rowId), []);

            const { visibleRows, context, onCellMouseDown } =
                useExpandableSubcategories(reviewData);

            const pinnedRows = React.useMemo(() => {
                const hasOriginalRow =
                    standardizedTotalNetIncomeOriginal !== null;
                const valuesMatch =
                    hasOriginalRow &&
                    standardizedTotalNetIncomeAltered !== null &&
                    columnDefsOperatingStatementsFromProps.every(
                        ({ field }) => {
                            const originalValue =
                                standardizedTotalNetIncomeOriginal[field] ??
                                null;
                            const alteredValue =
                                standardizedTotalNetIncomeAltered[field] ??
                                null;
                            return originalValue === alteredValue;
                        },
                    );
                return [
                    standardizedTotalNetIncomeAltered && {
                        rowId: "row-altered",
                        account: "Mapped Net Operating Income",
                        type: "TOTAL",
                        pinnedRowColor:
                            !isExtractionCompleted && valuesMatch
                                ? "green"
                                : "blue",
                        ...standardizedTotalNetIncomeAltered,
                    },
                    !isExtractionCompleted &&
                        standardizedTotalNetIncomeAltered && {
                            rowId: "row-original",
                            account:
                                standardizedTotalNetIncomeOriginal === null
                                    ? "Original Document - target not identified"
                                    : typeof standardizedOriginalNetIncomeTotalName ===
                                            "string" &&
                                        standardizedOriginalNetIncomeTotalName !==
                                            ""
                                      ? `Original Document - ${standardizedOriginalNetIncomeTotalName}`
                                      : "Original Document",
                            type: "TOTAL",
                            pinnedRowColor:
                                standardizedTotalNetIncomeOriginal === null
                                    ? "red"
                                    : "green",
                            ...(standardizedTotalNetIncomeOriginal ?? {}),
                        },
                ].filter(Boolean);
            }, [
                columnDefsOperatingStatementsFromProps,
                isExtractionCompleted,
                standardizedTotalNetIncomeAltered,
                standardizedTotalNetIncomeOriginal,
                standardizedOriginalNetIncomeTotalName,
            ]);

            return (
                <Grid<TRowReviewWithOperatingStatements>
                    ref={ref}
                    className={clsx([testId])}
                    gridTheme="ag-theme-custom-proda"
                    sx={{
                        ...makeOverlappingColumnHeadersWhite,
                        ...disableRowAnimationOnZooming,
                        ...extraStyles,
                        ...sx,
                    }}
                    context={context}
                    onCellMouseDown={onCellMouseDown}
                    rowData={visibleRows}
                    pinnedBottomRowData={pinnedRows}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={{
                        suppressHeaderMenuButton: true,
                        sortable: false,
                    }}
                    readOnlyEdit={true}
                    suppressHeaderFocus={true}
                    suppressMovableColumns={true}
                    enableBrowserTooltips={true}
                    focusGridInnerElement={focusFirstCellInAccountColumn}
                    rowStyle={{ backgroundColor: "none" }}
                    enterNavigatesVertically={true}
                    enterNavigatesVerticallyAfterEdit={true}
                    {...otherProps}
                />
            );
        },
    ),
);

GridReview.displayName = "GridReview";

export default GridReview;
GridReview.displayName = "GridReview";
